import { toast } from 'react-toastify';
import { useState, useEffect } from 'react';
import { useAuth } from '@/context/AuthContext';
import { 
    getAllPromotionalBookings,
    createPromotionalBooking, 
    updatePromotionalBooking, 
    deletePromotionalBooking,
    getAllPromotionalAreas,
    getAllCounterparties,
    getAllProperties
} from '@/services/marketingService';
import * as Sentry from '@sentry/react';

const PromotionalBookingsPage = () => {
    const [bookings, setBookings] = useState([]);
    const [promotionalAreas, setPromotionalAreas] = useState([]);
    const [counterparties, setCounterparties] = useState([]);
    const [properties, setProperties] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [showModal, setShowModal] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [selectedBooking, setSelectedBooking] = useState(null);
    const [viewMode, setViewMode] = useState('list');
    const [filterProperty, setFilterProperty] = useState('all');
    const [formData, setFormData] = useState({
        promotionalAreaId: '',
        counterpartyId: '',
        startDate: '',
        endDate: '',
        bookingStatus: 'Confirmed',
        invoiceStatus: 'NotInvoiced',
        paymentStatus: 'Unpaid',
        approvalStatus: 'NotRequired',
        comments: ''
    });

    const { apiFetch } = useAuth();

    useEffect(() => {
        fetchData();
    }, []);

    const fetchData = async () => {
        setIsLoading(true);
        try {
            const [bookingsData, areasData, counterpartiesData, propertiesData] = await Promise.all([
                getAllPromotionalBookings(apiFetch),
                getAllPromotionalAreas(apiFetch),
                getAllCounterparties(apiFetch),
                getAllProperties(apiFetch)
            ]);
            setBookings(bookingsData);
            setPromotionalAreas(areasData);
            setCounterparties(counterpartiesData);
            setProperties(propertiesData);
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to load data');
        } finally {
            setIsLoading(false);
        }
    };

    const handleOpenModal = (booking = null) => {
        if (booking) {
            setSelectedBooking(booking);
            setFormData({
                promotionalAreaId: booking.promotionalAreaId || '',
                counterpartyId: booking.counterpartyId || '',
                startDate: booking.startDate?.split('T')[0] || '',
                endDate: booking.endDate?.split('T')[0] || '',
                bookingStatus: booking.bookingStatus || 'Confirmed',
                invoiceStatus: booking.invoiceStatus || 'NotInvoiced',
                paymentStatus: booking.paymentStatus || 'Unpaid',
                approvalStatus: booking.approvalStatus || 'NotRequired',
                comments: booking.comments || ''
            });
        } else {
            setSelectedBooking(null);
            setFormData({
                promotionalAreaId: '',
                counterpartyId: '',
                startDate: '',
                endDate: '',
                bookingStatus: 'Confirmed',
                invoiceStatus: 'NotInvoiced',
                paymentStatus: 'Unpaid',
                approvalStatus: 'NotRequired',
                comments: ''
            });
        }
        setShowModal(true);
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setSelectedBooking(null);
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        if (!formData.promotionalAreaId || !formData.counterpartyId || 
            !formData.startDate || !formData.endDate) {
            toast.error('Please fill in all required fields');
            return;
        }

        if (new Date(formData.endDate) <= new Date(formData.startDate)) {
            toast.error('End date must be after start date');
            return;
        }

        setIsLoading(true);
        try {
            if (selectedBooking) {
                await updatePromotionalBooking(apiFetch, selectedBooking.promotionalBookingId, formData);
                toast.success('Promotional booking updated successfully');
            } else {
                await createPromotionalBooking(apiFetch, formData);
                toast.success('Promotional booking created successfully');
            }
            handleCloseModal();
            fetchData();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to save promotional booking');
        } finally {
            setIsLoading(false);
        }
    };

    const handleDeleteClick = (booking) => {
        setSelectedBooking(booking);
        setShowDeleteModal(true);
    };

    const handleDelete = async () => {
        if (!selectedBooking) return;

        setIsLoading(true);
        try {
            await deletePromotionalBooking(apiFetch, selectedBooking.promotionalBookingId);
            toast.success('Promotional booking deleted successfully');
            setShowDeleteModal(false);
            setSelectedBooking(null);
            fetchData();
        } catch (error) {
            Sentry.captureException(error);
            toast.error(error.message || 'Failed to delete promotional booking');
        } finally {
            setIsLoading(false);
        }
    };

    const getPromotionalAreaName = (id) => {
        const area = promotionalAreas.find(a => a.promotionalAreaId === id);
        return area?.name || 'Unknown';
    };

    const getCounterpartyName = (id) => {
        const cp = counterparties.find(c => c.Id === id);
        return cp?.CompanyName || 'Unknown';
    };

    const getPropertyIdForArea = (areaId) => {
        const area = promotionalAreas.find(a => a.promotionalAreaId === areaId);
        return area?.propertyId;
    };

    const getApprovalStatusBadge = (status) => {
        const badges = {
            'NotRequired': 'badge-secondary',
            'Pending': 'badge-warning',
            'Approved': 'badge-success',
            'Rejected': 'badge-danger'
        };
        return badges[status] || 'badge-secondary';
    };

    const filteredBookings = bookings.filter(booking => {
        const areaPropertyId = getPropertyIdForArea(booking.promotionalAreaId);
        return filterProperty === 'all' || areaPropertyId === parseInt(filterProperty);
    });

    const filteredAreasByProperty = promotionalAreas.filter(area => {
        return filterProperty === 'all' || area.propertyId === parseInt(filterProperty);
    });

    return (
        <div className="csi-container">
            <h1>Promotional Bookings</h1>

            <div className="toolbar">
                <div className="search-filter-group">
                    <select
                        value={filterProperty}
                        onChange={(e) => setFilterProperty(e.target.value)}
                        className="form-control filter-select"
                    >
                        <option value="all">All Properties</option>
                        {properties.map((prop, index) => (
                            <option key={prop.propertyID ?? prop.PropertyID ?? `prop-${index}`} value={prop.propertyID ?? prop.PropertyID}>
                                {prop.propertyName || prop.PropertyName}
                            </option>
                        ))}
                    </select>
                    <button 
                        className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setViewMode('list')}
                    >
                        List View
                    </button>
                    <button 
                        className={`btn ${viewMode === 'weekly' ? 'btn-primary' : 'btn-secondary'}`}
                        onClick={() => setViewMode('weekly')}
                    >
                        Weekly View
                    </button>
                </div>
                <button className="btn btn-primary" onClick={() => handleOpenModal()}>
                    Add Promotional Booking
                </button>
            </div>

            {isLoading ? (
                <div className="loading">Loading promotional bookings...</div>
            ) : (
                <div className="table-container">
                    <table className="data-table">
                        <thead>
                            <tr>
                                <th>Promotional Area</th>
                                <th>Counterparty</th>
                                <th>Start Date</th>
                                <th>End Date</th>
                                <th>Booking Status</th>
                                <th>Approval Status</th>
                                <th>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {filteredBookings.length === 0 ? (
                                <tr>
                                    <td colSpan="7" style={{ textAlign: 'center' }}>
                                        No promotional bookings found
                                    </td>
                                </tr>
                            ) : (
                                filteredBookings.map(booking => (
                                    <tr key={booking.promotionalBookingId}>
                                        <td>{getPromotionalAreaName(booking.promotionalAreaId)}</td>
                                        <td>{getCounterpartyName(booking.counterpartyId)}</td>
                                        <td>{new Date(booking.startDate).toLocaleDateString()}</td>
                                        <td>{new Date(booking.endDate).toLocaleDateString()}</td>
                                        <td>{booking.bookingStatus}</td>
                                        <td>
                                            <span className={`badge ${getApprovalStatusBadge(booking.approvalStatus)}`}>
                                                {booking.approvalStatus}
                                            </span>
                                        </td>
                                        <td>
                                            <button 
                                                className="btn btn-sm btn-secondary"
                                                onClick={() => handleOpenModal(booking)}
                                            >
                                                Edit
                                            </button>
                                            <button 
                                                className="btn btn-sm btn-danger"
                                                onClick={() => handleDeleteClick(booking)}
                                                style={{ marginLeft: '5px' }}
                                            >
                                                Delete
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            )}
                        </tbody>
                    </table>
                </div>
            )}

            {showModal && (
                <div className="modal-overlay">
                    <div className="modal-container" style={{ maxWidth: '600px' }}>
                        <div className="modal-header">
                            <h3>{selectedBooking ? 'Edit Promotional Booking' : 'Add Promotional Booking'}</h3>
                        </div>
                        <form onSubmit={handleSubmit}>
                            <div className="modal-body">
                                <div className="form-group">
                                    <label htmlFor="promotionalAreaId">Promotional Area *</label>
                                    <select
                                        id="promotionalAreaId"
                                        name="promotionalAreaId"
                                        value={formData.promotionalAreaId}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    >
                                        <option value="">Select Promotional Area</option>
                                        {filteredAreasByProperty.filter(a => a.isActive).map((area, index) => (
                                            <option key={area.promotionalAreaId ?? `area-${index}`} value={area.promotionalAreaId}>
                                                {area.name}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="counterpartyId">Counterparty *</label>
                                    <select
                                        id="counterpartyId"
                                        name="counterpartyId"
                                        value={formData.counterpartyId}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    >
                                        <option value="">Select Counterparty</option>
                                        {counterparties.filter(cp => cp.IsActive).map((cp, index) => (
                                            <option key={cp.Id ?? `modal-cp-${index}`} value={cp.Id}>
                                                {cp.CompanyName}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="startDate">Start Date *</label>
                                    <input
                                        type="date"
                                        id="startDate"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="endDate">End Date *</label>
                                    <input
                                        type="date"
                                        id="endDate"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label htmlFor="bookingStatus">Booking Status</label>
                                    <select
                                        id="bookingStatus"
                                        name="bookingStatus"
                                        value={formData.bookingStatus}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    >
                                        <option value="Provisional">Provisional</option>
                                        <option value="Confirmed">Confirmed</option>
                                        <option value="Cancelled">Cancelled</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="invoiceStatus">Invoice Status</label>
                                    <select
                                        id="invoiceStatus"
                                        name="invoiceStatus"
                                        value={formData.invoiceStatus}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    >
                                        <option value="NotInvoiced">Not Invoiced</option>
                                        <option value="Invoiced">Invoiced</option>
                                    </select>
                                </div>
                                <div className="form-group">
                                    <label htmlFor="paymentStatus">Payment Status</label>
                                    <select
                                        id="paymentStatus"
                                        name="paymentStatus"
                                        value={formData.paymentStatus}
                                        onChange={handleInputChange}
                                        className="form-control"
                                    >
                                        <option value="Unpaid">Unpaid</option>
                                        <option value="Paid">Paid</option>
                                        <option value="NoCharge">No Charge</option>
                                    </select>
                                    {formData.paymentStatus === 'NoCharge' && (
                                        <div className="alert alert-warning" style={{ marginTop: '10px' }}>
                                            ⚠️ Booking will be Provisional and require approval
                                        </div>
                                    )}
                                </div>
                                <div className="form-group">
                                    <label htmlFor="comments">Comments</label>
                                    <textarea
                                        id="comments"
                                        name="comments"
                                        value={formData.comments}
                                        onChange={handleInputChange}
                                        className="form-control"
                                        rows="3"
                                    />
                                </div>
                            </div>
                            <div className="modal-footer">
                                <button 
                                    type="button" 
                                    className="btn btn-secondary" 
                                    onClick={handleCloseModal}
                                    disabled={isLoading}
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit" 
                                    className="btn btn-primary"
                                    disabled={isLoading}
                                >
                                    {isLoading ? 'Saving...' : 'Save'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {showDeleteModal && (
                <div className="modal-overlay">
                    <div className="modal-container">
                        <div className="modal-header">
                            <h3>Confirm Delete</h3>
                        </div>
                        <div className="modal-body">
                            <p>Are you sure you want to delete this promotional booking?</p>
                        </div>
                        <div className="modal-footer">
                            <button 
                                className="btn btn-secondary" 
                                onClick={() => setShowDeleteModal(false)}
                                disabled={isLoading}
                            >
                                Cancel
                            </button>
                            <button 
                                className="btn btn-danger" 
                                onClick={handleDelete}
                                disabled={isLoading}
                            >
                                {isLoading ? 'Deleting...' : 'Delete'}
                            </button>
                        </div>
                    </div>
                </div>
            )}
</div>
    );
};

export default PromotionalBookingsPage;
